import {
  compareMeetingSessionRoles,
  MeetingsStateCreateParams,
  OvenMediaRetransmissionParams,
  OvenMediaRetransmissionSource,
  OvenMediaRetransmissionSrcFactoryContext,
} from "@kalyzee/kast-web-sdk";
import { MeetingSession, MeetingSessionHighlightingState, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";
import { matchEngine, templateEngine } from "./matchEngine";
import { SettingsRetransmissionDestination } from "./settings";

type SdkRetransmissionSrcExtractor = NonNullable<MeetingsStateCreateParams["retransmissionSrcExtractor"]>;
type MeetingStoreLike = Parameters<SdkRetransmissionSrcExtractor>[0];
type RetransmissionStoreState = ReturnType<MeetingStoreLike["getState"]>;

// Narrower return than the SDK's (always a list): keeps call sites simple
export type RetransmissionSrcExtractor = (store: MeetingStoreLike, source: OvenMediaRetransmissionSource) => OvenMediaRetransmissionParams[] | undefined;

/**
 * Context the `match` expressions of settings.retransmission.destinations are
 * applied to. session/media are re-resolved from the live meeting store on every
 * evaluation, so the expressions always see fresh roles/permissions/states.
 * Discriminated on `type`: stream sources always carry session/media, custom
 * sources carry their key ('audio-mixer'...).
 */
export type RetransmissionMatchContext = {
  /** My session. */
  currentSession?: RetransmissionStoreState["session"];
  meeting?: RetransmissionStoreState["meeting"];
  highlighted: boolean;
  /**
   * Urls only (meaningless in `match` conditions): fresh random key resolved at
   * every publish attempt (recreations/retries), so a republished stream never
   * reuses a name the server may not have released yet.
   */
  nonce?: string;
  /** Urls only: retry counter of the current publish attempt (0 = first). */
  attempt?: number;
  /**
   * Urls only: wallclock (epoch ms) at which this publish attempt started. Pass it
   * to the recording chain (e.g. `&clientPublishStartedAt={{attemptAt}}`) on EVERY
   * destination: all publishes of a page share this one clock, so the VOD player can
   * align the resulting files relatively to each other (see the VOD player page).
   */
  attemptAt?: number;
} & (
  | { type: "emission" | "reception"; session: MeetingSession; media: MeetingSessionMedia; key?: undefined }
  | { type: "custom"; key: string; session?: undefined; media?: undefined }
);

// MEETING_SESSION_ROLE_TO_INT is inverted (lower = more powerful): negate so that
// "session.role >= 'moderator'" reads naturally (same idiom as audio/board matches).
const ROLE_COMPARATOR = { "session.role": (a: any, b: any) => -compareMeetingSessionRoles(a, b) };

export const buildRetransmissionMatchContext = (
  state: RetransmissionStoreState,
  source: OvenMediaRetransmissionSource,
): RetransmissionMatchContext | undefined => {
  if (source.type === "custom") {
    return { type: source.type, key: source.key, currentSession: state.session, meeting: state.meeting, highlighted: false };
  }
  const session =
    source.type === "emission" ? state.session : state.sessions?.find((s) => s.id === source.session.id);
  const media = session?.medias?.find((m) => m.id === source.mediaSession.id);
  if (!session || !media) return undefined;
  return {
    type: source.type,
    session,
    media,
    currentSession: state.session,
    meeting: state.meeting,
    highlighted: session.highlighting === MeetingSessionHighlightingState.ONGOING,
  };
};

/**
 * A destination template ({@link SettingsRetransmissionDestination.url} or `srcKey`)
 * that could not be fully resolved against the live context: the retransmission is
 * skipped, so this is a config error worth surfacing to the user.
 */
export interface RetransmissionTemplateError {
  destinationId: string;
  /** Which template of the destination failed. */
  field: "url" | "srcKey";
  /** The configured template, unresolved placeholders included. */
  template: string;
  /** Dotted paths missing from the context, e.g. ["media.mediaSrc"]. */
  unresolved: string[];
  /** Partially substituted template: what the resolution actually produced. */
  partial: string;
  /** Context the template was resolved against (log payload). */
  context: unknown;
}

export type RetransmissionErrorReporter = (error: RetransmissionTemplateError) => void;

/**
 * The current kind on each media axis this app knows, as the SDK's
 * `retransmissionMediaStateExtractor` returns it (structurally the SDK's
 * `OvenMediaRetransmissionMediaState`). The stream axis is the SDK's own.
 */
export type RetransmissionMediaState = {
  presence?: "media_added" | "media_removed";
  video?: "video_on" | "video_off";
  audio?: "audio_on" | "audio_off";
};

export type RetransmissionMediaStateExtractor = (store: MeetingStoreLike, source: OvenMediaRetransmissionSource) => RetransmissionMediaState | undefined;

/**
 * What the recording chain is told about each stream this page republishes, so a
 * replay can say why a tile is black: whether the media is still in the meeting,
 * and whether its camera and mic are on. Read from the same live store the match
 * context uses, so a force-mute a moderator applied is seen exactly as the tile
 * shows it. A custom source (the classroom mixer) has no such state.
 */
export const buildRetransmissionMediaStateExtractor = (): RetransmissionMediaStateExtractor => {
  return (store, source) => {
    if (source.type === "custom") return undefined;
    const state = store.getState();
    const session = source.type === "emission" ? state.session : state.sessions?.find((s) => s.id === source.session.id);
    const media = session?.medias?.find((m) => m.id === source.mediaSession.id);
    if (!media) return { presence: "media_removed" };
    return {
      presence: "media_added",
      video: media.video?.enabled === false ? "video_off" : "video_on",
      audio: media.audio?.enabled === false ? "audio_off" : "audio_on",
    };
  };
};

/**
 * Builds the SDK retransmissionSrcExtractor from settings.retransmission.destinations.
 * Undefined when nothing is configured (feature disabled). The returned extractor is
 * pure: it matches every candidate source against each destination's matchEngine
 * expression and yields one SDK destination per match.
 *
 * `onError` is called for every template that stays unresolved (the destination is
 * then skipped): the caller decides how to surface it. It fires on every publish
 * attempt, so a reporter meant for the user must dedupe.
 */
export const buildRetransmissionSrcExtractor = (
  destinations: SettingsRetransmissionDestination[] | undefined,
  onError?: RetransmissionErrorReporter,
): RetransmissionSrcExtractor | undefined => {
  const validDestinations = (destinations ?? []).filter((d) => d?.id && d.match && d.url);
  if (!validDestinations.length) return undefined;
  return (store, source) => {
    const context = buildRetransmissionMatchContext(store.getState(), source);
    if (!context) return undefined;
    const params: OvenMediaRetransmissionParams[] = [];
    const resolve = (field: "url" | "srcKey", template: string, templateContext: unknown, destinationId: string) => {
      // jsonifyObjects: an object value (e.g. {{media.metadata}}) travels as JSON
      // in the url rather than "[object Object]"
      const { value, unresolved, partial } = templateEngine(templateContext, template, { jsonifyObjects: true });
      if (value === undefined) {
        onError?.({ destinationId, field, template, unresolved, partial, context: templateContext });
      }
      return value;
    };
    validDestinations.forEach((destination) => {
      if (!matchEngine(context, destination.match, ROLE_COMPARATOR)) return;
      params.push({
        id: destination.id,
        // resolved at every publish attempt with the whole call context spread in
        // (nonce, attempt...); the match context wins on collisions so {{key}} keeps
        // its match meaning (custom source key, not the destination key). Query params
        // travel in the url itself (they survive the emitter's own URL processing).
        src: (factoryContext: OvenMediaRetransmissionSrcFactoryContext) =>
          resolve("url", destination.url, { ...factoryContext, ...context }, destination.id),
        // Restart identity, opt-in from the config (a per-attempt src has none by
        // itself): its own template, resolved on THIS evaluation's context. A different
        // value than the running one restarts the publish towards the new target.
        srcKey: destination.srcKey ? resolve("srcKey", destination.srcKey, context, destination.id) : undefined,
        media: { audio: destination.audio !== false, video: destination.video !== false },
        // Left undefined when unconfigured so the SDK defaults apply (cloneTracks: true,
        // no empty-track completion). No preRequestMiddleware counterpart: the url is
        // already a per-attempt template ({{nonce}}, {{attempt}}), which covers the same
        // need without a config-side function.
        cloneTracks: destination.cloneTracks,
        fillMissingAudioTrack: destination.fillMissingAudioTrack,
        fillMissingVideoTrack: destination.fillMissingVideoTrack,
        earlyStart: destination.earlyStart,
        // Encoded passthrough of a received stream. Also read by the SDK on a receiver-less
        // reception source, before the receiver exists, to decide which encoded-frame
        // interceptors to create it with: the match must already hold at that point.
        bypassAudio: destination.bypassAudio,
        bypassVideo: destination.bypassVideo,
        sessionOptions: destination.sessionOptions,
        stopDelay: destination.stopDelay,
      });
    });
    return params.length ? params : undefined;
  };
};
